import React, { useState, useMemo } from 'react';
import {
    Box,
    Typography,
    IconButton,
    TextField,
    List,
    ListItem,
    ListItemText,
    Paper,
    InputAdornment,
    Chip,
    Stack,
    Divider,
    FormControl,
    Select,
    MenuItem,
    SelectChangeEvent,
    Button,
    useTheme,
    ToggleButtonGroup,
    ToggleButton,
} from '@mui/material';
import {
    Clear,
    FilterList,
    Search as SearchIcon,
    Download as DownloadIcon,
    Info as InfoIcon,
    Warning as WarningIcon,
    Error as ErrorIcon,
    Code as CodeIcon,
    Computer as ComputerIcon,
    Person as PersonIcon,
} from '@mui/icons-material';
import { Device } from '../../types/Device';
import { useLogging } from '../../contexts/LoggingContext';

interface LogViewerProps {
    device: Device;
}

// Log level types for filtering
type LogLevel = 'all' | 'info' | 'warning' | 'error';

// Log source filter
type LogSource = 'all' | 'device' | 'ui';

const LogViewer: React.FC<LogViewerProps> = ({ device }) => {
    const theme = useTheme();
    const { logs: uiLogs, getFormattedLog, clearLogs } = useLogging();
    const [filter, setFilter] = useState('');
    const [logLevel, setLogLevel] = useState<LogLevel>('all');
    const [logSource, setLogSource] = useState<LogSource>('all');

    // Filtra i log UI specifici per questo dispositivo
    const deviceUiLogs = useMemo(() => {
        return uiLogs
            .filter(log => !log.deviceId || log.deviceId === device.id)
            .map(log => ({
                text: getFormattedLog(log),
                level: log.level,
                source: 'ui',
                timestamp: log.timestamp,
                raw: log
            }));
    }, [uiLogs, device.id, getFormattedLog]);

    // Converti i log del dispositivo di backend in formato comune
    const deviceBackendLogs = useMemo(() => {
        return (device.logs || []).map(log => {
            const level = getLogLevel(log);
            return {
                text: log,
                level,
                source: 'device',
                timestamp: getLogTimestamp(log) || new Date().toISOString(),
                raw: log
            };
        });
    }, [device.logs]);

    // Unisci e ordina tutti i log per timestamp (più recenti in cima)
    const allLogs = useMemo(() => {
        return [...deviceUiLogs, ...deviceBackendLogs]
            .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    }, [deviceUiLogs, deviceBackendLogs]);

    const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFilter(event.target.value);
    };

    const handleClearFilter = () => {
        setFilter('');
    };

    const handleLogLevelChange = (event: SelectChangeEvent<LogLevel>) => {
        setLogLevel(event.target.value as LogLevel);
    };

    const handleLogSourceChange = (_event: React.MouseEvent<HTMLElement>, newSource: LogSource | null) => {
        if (newSource !== null) {
            setLogSource(newSource);
        }
    };

    // Mock data for log levels - in a real app, we'd parse this from the log content
    const getLogLevel = (log: string): LogLevel => {
        const lowerLog = log.toLowerCase();
        if (lowerLog.includes('error') || lowerLog.includes('exception') || lowerLog.includes('failed')) return 'error';
        if (lowerLog.includes('warn') || lowerLog.includes('warning')) return 'warning';
        return 'info';
    };

    // Filter logs by search text, log level, and source
    const filteredLogs = allLogs.filter(log => {
        // Filter by log source
        if (logSource !== 'all' && log.source !== logSource) return false;
        
        // Filter by log level
        if (logLevel !== 'all' && log.level !== logLevel) return false;
        
        // Filter by search text
        if (filter && !log.text.toLowerCase().includes(filter.toLowerCase())) return false;
        
        return true;
    });

    // Get log level icon
    const getLogLevelIcon = (level: LogLevel) => {
        switch (level) {
            case 'error':
                return <ErrorIcon fontSize="small" sx={{ color: theme.palette.error.main }} />;
            case 'warning':
                return <WarningIcon fontSize="small" sx={{ color: theme.palette.warning.main }} />;
            case 'info':
            default:
                return <InfoIcon fontSize="small" sx={{ color: theme.palette.info.main }} />;
        }
    };

    // Get log source icon
    const getLogSourceIcon = (source: string) => {
        switch (source) {
            case 'ui':
                return <PersonIcon fontSize="small" sx={{ color: theme.palette.secondary.main }} />;
            case 'device':
                return <ComputerIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />;
            default:
                return <InfoIcon fontSize="small" sx={{ color: theme.palette.info.main }} />;
        }
    };

    // Extract timestamp from log (assuming format like "2023-03-15 14:32:45 - Log message")
    const getLogTimestamp = (log: string): string => {
        const match = log.match(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/);
        return match ? match[0] : '';
    };

    // Extract message from log
    const getLogMessage = (log: string): string => {
        const timestamp = getLogTimestamp(log);
        return timestamp ? log.substring(timestamp.length + 3) : log;
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CodeIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                    <Typography variant="h6">
                        Device Logs
                    </Typography>
                </Box>
                
                <Stack direction="row" spacing={1}>
                    <Button 
                        size="small" 
                        startIcon={<Clear />} 
                        variant="outlined"
                        color="warning"
                        onClick={() => clearLogs()}
                    >
                        Clear Logs
                    </Button>
                    <Button 
                        size="small" 
                        startIcon={<DownloadIcon />} 
                        variant="outlined"
                        disabled={!filteredLogs.length}
                        onClick={() => {
                            // Create a text blob with all logs
                            const logText = filteredLogs.map(log => log.text).join('\n');
                            const blob = new Blob([logText], { type: 'text/plain' });
                            const url = URL.createObjectURL(blob);
                            
                            // Create download link and trigger click
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `device_${device.id}_logs_${new Date().toISOString().slice(0, 10)}.txt`;
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                            URL.revokeObjectURL(url);
                        }}
                    >
                        Export Logs
                    </Button>
                </Stack>
            </Box>

            <Box sx={{ mb: 3, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
                {/* Search Filter */}
                <TextField
                    placeholder="Filter logs..."
                    variant="outlined"
                    size="small"
                    value={filter}
                    onChange={handleFilterChange}
                    sx={{ flexGrow: 1 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                        endAdornment: filter && (
                            <InputAdornment position="end">
                                <IconButton 
                                    size="small" 
                                    onClick={handleClearFilter}
                                    edge="end"
                                >
                                    <Clear fontSize="small" />
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />

                <Box sx={{ display: 'flex', gap: 2 }}>
                    {/* Log Source Filter */}
                    <ToggleButtonGroup
                        value={logSource}
                        exclusive
                        onChange={handleLogSourceChange}
                        aria-label="log source"
                        size="small"
                    >
                        <ToggleButton value="all" aria-label="all sources">
                            All
                        </ToggleButton>
                        <ToggleButton value="ui" aria-label="ui logs">
                            UI
                        </ToggleButton>
                        <ToggleButton value="device" aria-label="device logs">
                            Device
                        </ToggleButton>
                    </ToggleButtonGroup>

                    {/* Log Level Filter */}
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <Select
                            value={logLevel}
                            onChange={handleLogLevelChange}
                            displayEmpty
                            renderValue={(value) => (
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <FilterList fontSize="small" sx={{ mr: 1 }} />
                                    {value === 'all' ? 'All Levels' : value.charAt(0).toUpperCase() + value.slice(1)}
                                </Box>
                            )}
                        >
                            <MenuItem value="all">All Levels</MenuItem>
                            <MenuItem value="info">Info</MenuItem>
                            <MenuItem value="warning">Warning</MenuItem>
                            <MenuItem value="error">Error</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
            </Box>

            {/* Results summary */}
            <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                    Showing {filteredLogs.length} of {allLogs.length} logs
                    {filter && (
                        <Chip 
                            label={`Search: ${filter}`} 
                            size="small" 
                            onDelete={handleClearFilter} 
                            sx={{ ml: 1 }}
                        />
                    )}
                    {logLevel !== 'all' && (
                        <Chip 
                            label={`Level: ${logLevel}`}
                            size="small"
                            onDelete={() => setLogLevel('all')}
                            sx={{ ml: 1 }}
                        />
                    )}
                    {logSource !== 'all' && (
                        <Chip 
                            label={`Source: ${logSource}`}
                            size="small"
                            onDelete={() => setLogSource('all')}
                            sx={{ ml: 1 }}
                        />
                    )}
                </Typography>
            </Box>

            {/* Logs List */}
            {filteredLogs.length > 0 ? (
                <Paper
                    variant="outlined"
                    sx={{
                        maxHeight: 400,
                        overflow: 'auto',
                        bgcolor: 'background.default',
                        borderRadius: 2,
                    }}
                >
                    <List dense disablePadding>
                        {filteredLogs.map((log, index) => {
                            // Per log da backend, estrai messaggio e timestamp
                            let message = log.source === 'device' ? getLogMessage(log.text) : log.text;
                            
                            return (
                                <React.Fragment key={index}>
                                    {index > 0 && <Divider />}
                                    <ListItem
                                        alignItems="flex-start"
                                        sx={{
                                            py: 1.5,
                                            px: 2,
                                            '&:hover': {
                                                bgcolor: 'rgba(255, 255, 255, 0.05)',
                                            },
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
                                            {/* Log Source + Level Icons */}
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 0.5, mr: 1.5, gap: 0.5 }}>
                                                {getLogLevelIcon(log.level)}
                                                {getLogSourceIcon(log.source)}
                                            </Box>

                                            {/* Log Content */}
                                            <Box sx={{ flex: 1 }}>
                                                <Typography 
                                                    variant="body2" 
                                                    component="div"
                                                    sx={{ 
                                                        fontFamily: 'monospace',
                                                        wordBreak: 'break-word',
                                                    }}
                                                >
                                                    {message}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </ListItem>
                                </React.Fragment>
                            );
                        })}
                    </List>
                </Paper>
            ) : (
                <Box
                    sx={{
                        p: 4,
                        textAlign: 'center',
                        bgcolor: 'background.default',
                        borderRadius: 2,
                    }}
                >
                    {allLogs.length ? (
                        <Typography color="text.secondary">
                            No logs match your filters. Try changing your search criteria.
                        </Typography>
                    ) : (
                        <Typography color="text.secondary">
                            No logs available for this device.
                        </Typography>
                    )}
                </Box>
            )}
        </Box>
    );
};

export default LogViewer; 